import {addCircularTabFocus, focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {ArrowLeftSmall, ArrowRightSmall} from '@wix/wix-ui-icons-common'
import {HoverStyles, TextButton} from '@wix/wix-ui-tpa/cssVars'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {PropsWithChildren, useEffect} from 'react'
import {SlideInModalProps} from './interfaces'
import {classes} from './slide-in-modal.st.css'

export const ANIMATION_SPEED = 200 // should match animation-speed in st.css

export const SlideInModal = ({
  id,
  className,
  children,
  show,
  backButtonText,
  fromBottom,
  animations = true,
  immediate = false,
  focus,
  circularFocusStart,
  circularFocusEnd,
  onBackClick,
  dataHook,
  ariaModal = false,
}: PropsWithChildren<SlideInModalProps>) => {
  const {isRTL, isMobile} = useEnvironment()
  const {t} = useTranslation()
  const hidden = !show
  const backButtonDH = `${id}-${DH.BACK}`

  const showBorder = !isMobile

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        focusElement({selector: hookToAttributeSelector(focus || backButtonDH), canFocus: !focus})
      }, ANIMATION_SPEED)
    }
  }, [show, focus])

  useEffect(() => {
    if (show) {
      const removeCircularFocus = addCircularTabFocus(
        circularFocusStart || hookToAttributeSelector(backButtonDH),
        circularFocusEnd || hookToAttributeSelector(DH.CLOSE_BUTTON),
      )

      return () => removeCircularFocus?.()
    }
  }, [show, circularFocusStart, circularFocusEnd])

  return (
    <div
      className={classNames(classes.container, className, {
        [classes.fromBottom]: fromBottom,
        [classes.show]: show,
        [classes.hide]: hidden,
        [classes.animations]: animations,
        [classes.immediate]: immediate,
        [classes.borderRight]: showBorder && !isRTL,
        [classes.borderLeft]: showBorder && isRTL,
      })}
      role="dialog"
      aria-hidden={hidden}
      aria-modal={ariaModal}
      data-hook={dataHook}
    >
      {onBackClick ? (
        <div className={id === 'tickets' ? classes.ticketsHeader : classes.header}>
          <TextButton
            prefixIcon={isRTL ? <ArrowRightSmall /> : <ArrowLeftSmall />}
            className={classes.backButton}
            onClick={onBackClick}
            data-hook={backButtonDH}
            hoverStyle={HoverStyles.underline}
          >
            {backButtonText ?? t('back')}
          </TextButton>
        </div>
      ) : null}
      {children}
    </div>
  )
}
